import { PlusCircleFilled, SearchOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import { Button, Col, Row, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fetchGroupsWithDescription, fetchUserGroups } from '../../api/groups';
import { Grouplist } from '../../components';
import { SearchInput } from '../../components/SearchInput';
import { GroupDetails, LoggedInUser } from '../../constants/types';
import { arrayMove } from '../../utils/arrayMove';
import { getParentOrigin } from '../../utils/getKeycloakConfig';
import { PageHeader } from '../styles';

//  Get all groups
const StyledSearchInput = styled(props => <SearchInput {...props} />)``;

const UserGroups = () => {
  const intl = useIntl();
  const { keycloak } = useKeycloak();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const newGroupId = query.get('newGroup');
  const updatedGroupId = query.get('updatedGroup');
  const [groupData, setGroupData] = useState<GroupDetails[]>([]);
  const [filteredGroupData, setFilteredGroupData] = useState<GroupDetails[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isRealmAdmin, setIsRealmAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groupIdToHighlight] = useState<string | null>(
    newGroupId || updatedGroupId,
  );

  const history = useHistory();
  const parentOrigin = getParentOrigin();

  const getGroups = useCallback(async () => {
    setLoading(true);

    try {
      let groups: GroupDetails[] = await fetchGroupsWithDescription();
      if (groupIdToHighlight) {
        const updatedUserIndex = groups.findIndex(
          (group: GroupDetails) => group.id === groupIdToHighlight,
        );

        groups = arrayMove(groups, updatedUserIndex);
      }
      setGroupData(groups);
    } catch (error: any) {
      message.error(error?.data?.errorMessage ?? error?.data?.errors?.[0]);
    }
    setLoading(false);
  }, [groupIdToHighlight]);

  useEffect(() => {
    getGroups();
  }, [getGroups]);

  useEffect(() => {
    const filterList = groupData.filter(
      group =>
        group.name
          .toLocaleLowerCase()
          .indexOf(searchTerm.trim().toLocaleLowerCase()) !== -1 ||
        group
          .description!.toLocaleLowerCase()
          .indexOf(searchTerm.trim().toLocaleLowerCase()) !== -1,
    );
    setFilteredGroupData([...filterList]);
  }, [groupData, searchTerm]);

  // Get user groups
  useEffect(() => {
    (async () => {
      try {
        const { sub } = (await keycloak.loadUserInfo()) as LoggedInUser;
        const data = await fetchUserGroups(sub);

        const hasTenantAdmin = data.some(
          group => group.name === 'tenant-admin',
        );

        setIsRealmAdmin(hasTenantAdmin);
      } catch (error: any) {
        message.error(error?.data?.error);
      }
    })();
  }, [keycloak]);

  const onSearch = async (keyword: string) => {
    setLoading(true);
    setSearchTerm(keyword);
    const filterList = await groupData.filter(
      group =>
        group.name
          .toLocaleLowerCase()
          .indexOf(keyword.trim().toLocaleLowerCase()) !== -1 ||
        group
          .description!.toLocaleLowerCase()
          .indexOf(keyword.trim().toLocaleLowerCase()) !== -1,
    );
    setFilteredGroupData([...filterList]);
    setLoading(false);
  };

  const redirectToCreateGroup = () => {
    if (parentOrigin && window.top && window.top !== window) {
      window.top.postMessage(
        {
          type: 'redirect',
          url: `/admin/usergroups/create`,
        },
        parentOrigin,
      );
    } else {
      history.push('/groups/create');
    }
  };

  return (
    <>
      <Row className="pt-28">
        <Col span={24}>
          <PageHeader
            gutter={[16, 8]}
            align="middle"
            justify="space-between"
            wrap
            className="mxi-0"
          >
            <Col xxl={{ span: 7 }} xs={{ span: 10 }}>
              <StyledSearchInput
                testId="usergroups-search-group"
                placeholder={intl.formatMessage({
                  id: 'UserGroup.placeholder.searchByUser',
                })}
                value={searchTerm}
                prefix={<SearchOutlined />}
                onChange={onSearch}
                hideClearAll
              />
            </Col>
            <Col>
              <Button
                shape="round"
                size="large"
                type="default"
                icon={<PlusCircleFilled />}
                className="action-btn"
                data-testid="usergroups-add-usergroup"
                onClick={redirectToCreateGroup}
              >
                <FormattedMessage id="UserGroup.btn.addUserGroup" />
              </Button>
            </Col>
          </PageHeader>
        </Col>
        <Col span={24} className="px-30">
          <Grouplist
            data={searchTerm.length ? filteredGroupData : groupData}
            loading={loading}
            isRealmAdmin={isRealmAdmin}
            groupIdToHighlight={groupIdToHighlight}
            getGroups={getGroups}
          />
        </Col>
      </Row>
    </>
  );
};

export default UserGroups;
